import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { apiService } from '../services/api';

interface SubscriptionState {
  isPremium: boolean;
  loading: boolean;
  plan: string | null;
  source: string | null;
  premiumUntil: string | null;
  prices: { monthly: number; yearly: number; currency: string };
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionState>({
  isPremium: false,
  loading: true,
  plan: null,
  source: null,
  premiumUntil: null,
  prices: { monthly: 99, yearly: 1099, currency: 'inr' },
  refresh: async () => {},
});

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<string | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [premiumUntil, setPremiumUntil] = useState<string | null>(null);
  const [prices, setPrices] = useState({ monthly: 99, yearly: 1099, currency: 'inr' });

  const refresh = useCallback(async () => {
    if (!isAuthenticated) { setIsPremium(false); setLoading(false); return; }
    try {
      setLoading(true);
      const res = await apiService.getSubscriptionStatus();
      if (res.success) {
        setIsPremium(!!res.isPremium);
        setPlan(res.plan || null);
        setSource(res.source || null);
        setPremiumUntil(res.premiumUntil || null);
        if (res.plans) setPrices(res.plans);
      }
    } catch (e) {
      setIsPremium(false);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <SubscriptionContext.Provider value={{ isPremium, loading, plan, source, premiumUntil, prices, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
};
